/**
 * Ported from the MEIS-13490 prototype's `agentSpeechPlayback.ts` (commit
 * fba1d661c2, "control reading aloud from each answer's speaker instead of
 * the header"): the playback state machine behind each message's own
 * "Read aloud" action — play, pause, resume, restart from the top — but
 * per-message, not a single header control (the header speaker switch is
 * gone; see `AgentPanel.tsx`). The actual audio (Web Audio scheduling,
 * fetches) lives in `agentSpeechOutput.ts`; this module only holds the pure
 * state machine and what a click on a given message's action does, so both
 * are unit-testable without a browser audio stack. The prototype's
 * `messageSpeechLabel` returned hardcoded German text; `messageSpeechLabelKind`
 * instead returns which of the product's own Tolgee keys applies, chosen by
 * the caller (`AgentChatBox.tsx`).
 */

export type SpeechPlaybackState =
  | "idle"
  | "loading"
  | "playing"
  | "paused"
  | "ended";

export type SpeechPlaybackEventType =
  /** A new read request (a message's "Read aloud", autoread). */
  | "speak"
  /** The first piece of audio was scheduled and started playing. */
  | "first-audio"
  | "pause"
  | "resume"
  /** Replay from the kept samples (only meaningful once ended). */
  | "replay"
  /** All scheduled audio finished playing after the text stream itself ended. */
  | "finished"
  | "stop";

const TRANSITIONS: Record<
  SpeechPlaybackState,
  Partial<Record<SpeechPlaybackEventType, SpeechPlaybackState>>
> = {
  idle: { speak: "loading" },
  // "finished" while still loading: the session ended before any audio
  // ever started (an empty/failed last piece) — done all the same, not a
  // no-op that leaves the UI stuck showing "loading".
  loading: {
    speak: "loading",
    "first-audio": "playing",
    finished: "ended",
    stop: "idle",
  },
  playing: {
    speak: "loading",
    pause: "paused",
    finished: "ended",
    stop: "idle",
  },
  paused: { speak: "loading", resume: "playing", stop: "idle" },
  ended: { speak: "loading", replay: "playing", stop: "idle" },
};

/**
 * The next playback state for `event`, or the same state when `event` makes
 * no sense right now (e.g. "resume" while already playing).
 */
export function nextSpeechPlaybackState(
  state: SpeechPlaybackState,
  event: SpeechPlaybackEventType
): SpeechPlaybackState {
  return TRANSITIONS[state][event] ?? state;
}

export type MessageSpeechAction = "pause" | "resume" | "restart";

/**
 * What a click on a message's own "Read aloud" action does: pause it while
 * it plays, resume it while paused — anything else (never started, already
 * ended, or a different message is the one currently active) starts this
 * message from the beginning, stopping whatever else was playing.
 */
export function messageSpeechAction(
  isActive: boolean,
  state: SpeechPlaybackState
): MessageSpeechAction {
  if (isActive && state === "playing") return "pause";
  if (isActive && state === "paused") return "resume";
  return "restart";
}

export type MessageSpeechLabelKind = "pause" | "resume" | "read-again" | "read";

/**
 * Which of the product's Tolgee keys the action's label (also its tooltip)
 * should use: only a message that is itself currently playing, paused or
 * just finished reads any differently from the plain "Read aloud".
 */
export function messageSpeechLabelKind(
  isActive: boolean,
  state: SpeechPlaybackState
): MessageSpeechLabelKind {
  if (isActive && state === "playing") return "pause";
  if (isActive && state === "paused") return "resume";
  if (isActive && state === "ended") return "read-again";
  return "read";
}

/**
 * Whether `candidateKey` names the message the current reading is about:
 * the key it was opened with, or the stored answer id learned for it once
 * the answer was stored. A chat message's own id can change under a reading
 * in progress (a history reload lists the answer under its stored id)
 * without the reading restarting — the speaker then still pauses it
 * (MEIS-13490 prototype, 29.9.: the first click restarted instead). Pure.
 */
export function matchesSpeechKey(
  candidateKey: string,
  activeKey: string | null,
  activeKeyAlias: string | null
): boolean {
  return candidateKey === activeKey || candidateKey === activeKeyAlias;
}

/**
 * Whether starting a message over can just replay its kept PCM samples (no
 * new request, no new cost) instead of asking the backend again: only when
 * it is the same message that just finished and its audio is still kept.
 */
export function canReplayFromKept(
  isSameKey: boolean,
  state: SpeechPlaybackState,
  hasKeptBuffers: boolean
): boolean {
  return isSameKey && state === "ended" && hasKeptBuffers;
}
